import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  makeStyles,
} from '@material-ui/core';
import { Content, Page, Progress } from '@backstage/core-components';
import ImportForm from './ImportForm';
import RequirementTable from './RequirementTable';
import AnalysisReview from './AnalysisReview';
import ManualEditor from './ManualEditor';
import AnalysisProgress from './AnalysisProgress';
import FixOptionsScreen from './FixOptionsScreen';
import ApplyingFixesScreen from './ApplyingFixesScreen';
import ForceApprovalDialog from './ForceApprovalDialog';
import ApproveConfirmDialog from './ApproveConfirmDialog';
import RedundancyProposals from './RedundancyProposals';
import { useRequirementStore } from './store/useRequirementStore';
import { useRewriteProposal } from './store/useRewriteProposal';
import type { ForceApproveOutcome } from './store/useRequirementStore';

/** The gate-blocked shape of ForceApproveOutcome: the only one with warnings. */
type GateBlocked = Extract<
  ForceApproveOutcome,
  { approved: false; reason: 'gate' }
>;
import { ApprovalWarningsDialog } from '../requirement-lifecycle/ApprovalWarningsDialog';
import type { FixAction, Requirement } from './types';
import * as api from './api/api';
import * as lifecycleApi from '../requirement-lifecycle/api';
import { useSetTopBarTitle } from '../shell/TopBarTitleContext';
import {
  ActiveProjectBar,
  RequireActiveProject,
  useActiveProject,
} from '../activeProject';
import { canPerform, useProjectRole } from '../../roles';

const useStyles = makeStyles(theme => ({
  alertError: {
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: theme.palette.error.light,
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.error.dark,
  },
  alertWarning: {
    marginBottom: '16px',
    padding: '12px',
    paddingRight: '40px',
    backgroundColor: theme.palette.warning.light,
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.warning.dark,
    position: 'relative' as const,
  },
  alertSuccess: {
    marginBottom: '16px',
    padding: '12px',
    paddingRight: '40px',
    backgroundColor: theme.palette.success.light,
    border: `1px solid ${theme.palette.success.main}`,
    borderRadius: theme.shape.borderRadius,
    position: 'relative' as const,
  },
  alertCloseButton: {
    position: 'absolute' as const,
    top: '8px',
    right: '8px',
    background: 'none',
    border: 'none',
    fontSize: '18px',
    lineHeight: 1,
    cursor: 'pointer',
    color: 'inherit',
  },
  contentCard: {
    backgroundColor: theme.palette.background.paper,
    padding: '24px',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    minWidth: 900,
  },
}));

type Screen =
  // Nothing is known about the project yet. Going straight to 'import' would
  // claim the project is empty before the list has even been asked for, and the
  // user would watch it flash past on every visit to a project that has data.
  | 'loading'
  | 'import'
  | 'select'
  | 'analyzing'
  | 'review'
  | 'fixOptions'
  | 'applyingFixes'
  | 'edit'
  // AI redundancy detection + merge proposals.
  | 'redundancies';

// Notification is stored as a translation descriptor (key + params) rather than
// a pre-resolved string, so the banner re-renders in the active language when
// the user toggles EN/JA instead of freezing the language at the time it was set.
// detail carries a message the backend produced, shown verbatim next to the
// translated text. Some failures (409 thread-not-editable, for one) are only
// explicable by what the server said.
type Notification = {
  key: string;
  params?: Record<string, unknown>;
  detail?: string;
};

interface RequirementDoctorViewProps {
  projectId: number;
}

const RequirementDoctorView: React.FC<RequirementDoctorViewProps> = ({
  projectId,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-doctor');
  const location = useLocation();
  // The backend refuses what the role does not allow; hiding those actions
  // keeps the screen from offering a button that can only end in a 403.
  const { activeProject } = useActiveProject();
  const projectRole = useProjectRole(activeProject);
  const canWrite = canPerform(projectRole, 'work-item.write');
  const canApprove = canPerform(projectRole, 'work-item.approve');
  const [screen, setScreen] = useState<Screen>('loading');
  const [message, setMessage] = useState<Notification | null>(null);
  const [selectedFixActions, setSelectedFixActions] = useState<FixAction[]>([]);
  // S7: the text saved by Accept when starting its re-analysis then failed.
  // Accepting that same text again retries only the analysis, instead of
  // saving a duplicate version.
  const [savedForAnalysis, setSavedForAnalysis] = useState<string | null>(null);
  const [forceApprovalOpen, setForceApprovalOpen] = useState(false);
  // Requirements the ASPICE fast-check gate refused to approve, with the
  // warnings it raised. Non-empty while that dialog is up.
  const [gateBlocked, setGateBlocked] = useState<GateBlocked[]>([]);
  const [approveConfirmId, setApproveConfirmId] = useState<string | null>(null);
  // Ids queued for deletion, awaiting confirmation in the delete dialog.
  // null = dialog closed.
  const [pendingDelete, setPendingDelete] = useState<string[] | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  // Transient polling errors (network, 5xx). Shown as a dismissible warning
  // and auto-cleared after 5 s so they don't persist like real auth failures.
  const [pollingError, setPollingError] = useState<string | null>(null);
  // Redundancy detection + merge proposals.
  const [redundancyLoading, setRedundancyLoading] = useState(false);
  const [redundancyError, setRedundancyError] = useState<string | null>(null);
  const [redundancyProposals, setRedundancyProposals] = useState<
    api.MergeProposal[]
  >([]);
  // Requirements the user asked to compare that the server excluded.
  const [excludedFromRedundancy, setExcludedFromRedundancy] = useState<
    Requirement[]
  >([]);
  const [mergeBusy, setMergeBusy] = useState(false);
  const store = useRequirementStore();
  const rewrite = useRewriteProposal();

  // Set the topbar title/subtitle for this page.
  // Reset to S0 whenever the user navigates to this route via the sidebar
  // (React Router sets a new location.key on each Link click, even to the
  // same path, so this fires only on genuine navigation, not on re-renders).
  useEffect(() => {
    setScreen('loading');
    setMessage(null);
    setSelectedFixActions([]);
    setForceApprovalOpen(false);
    setAuthError(null);
    setPollingError(null);
    setPendingDelete(null);
    setApproveConfirmId(null);
    store.setCurrentReqId(null);
    rewrite.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Load the project's existing requirements whenever the screen is entered.
  // Before this the Doctor only knew about what the current session had
  // imported, so requirements added earlier were unreachable and leaving the
  // page lost the list entirely.
  //
  // Landing on the select screen when the project already has requirements is
  // the point: starting on Import would suggest there is nothing there.
  const { loadProjectRequirements, setCurrentReqId } = store;
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const count = await loadProjectRequirements(projectId);
      if (cancelled) return;
      // A failed load also lands on Import — with the error banner above it, so
      // the empty form is not mistaken for an empty project. A role that cannot
      // import lands on the (empty) table instead.
      const hasRequirements = count !== null && count > 0;
      setScreen(hasRequirements || !canWrite ? 'select' : 'import');
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, location.key, loadProjectRequirements, canWrite]);

  // Auto-clear transient polling errors after 5 s.
  useEffect(() => {
    if (!pollingError) return undefined;
    const timer = setTimeout(() => setPollingError(null), 5000);
    return () => clearTimeout(timer);
  }, [pollingError]);

  // The requirements this run is about: exactly the rows ticked on the select
  // screen. The review screen used to be handed store.requirements, i.e. the
  // whole project, so rows that were never selected — and therefore never
  // analysed — appeared among the results. Acting on one of those ends in a
  // 400 analysis-not-run the screen cannot recover from.
  const selectedRequirements = store.requirements.filter(r =>
    store.selectedIds.has(r.id),
  );

  // The requirement currently being fixed/edited (S5–S7). Resolved once and
  // reused so every screen targets the same record (and its real backend ids).
  // The fallback stays inside the analysis targets for the same reason the
  // review list does.
  const currentRequirement =
    store.requirements.find(r => r.id === store.currentReqId) ??
    selectedRequirements[0];

  // Returning to the table always drops the requirement the detail screens were
  // pointing at. It is set from the review screen as well as from fix options,
  // and a leftover value changes what the table's own actions operate on — a
  // force-approve of several selected rows would silently approve only that
  // one instead.
  const backToSelect = useCallback(() => {
    setCurrentReqId(null);
    setScreen('select');
  }, [setCurrentReqId]);

  const handleImport = async (results: api.ImportRequirementResult[]) => {
    // Don't advance to the (empty) select screen if nothing was imported.
    if (results.length === 0) {
      setMessage({ key: 's0.validation.empty' });
      return;
    }
    // Show the imported rows straight away, then reconcile with the server so
    // the list is the project's requirements rather than just this import —
    // and so every row carries the same server-derived id.
    store.setRequirementsFromImport(results);
    store.selectAll(false);
    setMessage({
      key: 'common.messages.imported',
      params: { count: results.length },
    });
    backToSelect();
    await store.loadProjectRequirements(projectId);
  };

  const handleAnalyze = async () => {
    const selectedReqs = Array.from(store.selectedIds);
    const ok = await store.startAnalysisSession(selectedReqs, projectId);
    if (ok) {
      setScreen('analyzing');
    } else {
      setMessage({ key: 'common.messages.error' });
    }
  };

  // Run AI redundancy detection.
  //
  // Scoped to the current selection, or the whole project when nothing is
  // selected — which is the behaviour the ticket asks for and is only possible
  // now that the list is loaded from the project rather than from an import.
  const handleDetectRedundancies = async () => {
    const selected = store.requirements.filter(r =>
      store.selectedIds.has(r.id),
    );
    const scopedThreadIds = selected
      .map(r => r.threadId)
      .filter((id): id is string => typeof id === 'string');

    setScreen('redundancies');
    setRedundancyLoading(true);
    setRedundancyError(null);
    setRedundancyProposals([]);
    setExcludedFromRedundancy([]);
    try {
      const result = await api.detectRedundancies(
        projectId,
        scopedThreadIds.length > 0 ? scopedThreadIds : undefined,
      );
      setRedundancyProposals(result.proposals);
      // Membership check, not a count: the server deduplicates, so the two
      // lists can differ in length even when nothing was dropped.
      const analyzed = new Set(result.analyzedThreadIds.map(String));
      // Report against the whole list when nothing was selected — the backend
      // only compares WIP threads, and freshly imported requirements are DRAFT,
      // so an unscoped run on a new project compares nothing at all. Without
      // this the screen would call that "no redundancies found".
      const candidates = selected.length > 0 ? selected : store.requirements;
      setExcludedFromRedundancy(
        candidates.filter(r => r.threadId && !analyzed.has(r.threadId)),
      );
    } catch (error) {
      setRedundancyError(
        error instanceof Error ? error.message : String(error),
      );
    } finally {
      setRedundancyLoading(false);
    }
  };

  // Turn a proposal into a real merge request. From here the merged
  // requirement is PENDING_MERGE, the sources are locked, and the lifecycle
  // screen's existing review UI
  // takes over — it opens on that state, so no extra handoff
  // screen is needed beyond sending the user there.
  const handleMergeProposal = async (proposal: api.MergeProposal) => {
    setMergeBusy(true);
    setRedundancyError(null);
    try {
      await lifecycleApi.proposeMerge({
        projectId,
        sourceThreadIds: proposal.sourceThreadIds.map(String),
        name: proposal.mergedText.slice(0, 80),
        text: proposal.mergedText,
      });
      setMessage({ key: 'redundancy.messages.mergeProposed' });
      backToSelect();
      await store.loadProjectRequirements(projectId);
    } catch (error) {
      setRedundancyError(
        error instanceof Error ? error.message : String(error),
      );
    } finally {
      setMergeBusy(false);
    }
  };

  // RequirementTable calls this with no args — queue all selected ids for
  // deletion, pending confirmation.
  const handleDeleteSelected = () => {
    const ids = Array.from(store.selectedIds);
    if (ids.length === 0) return;
    setPendingDelete(ids);
  };

  // AnalysisReview calls this with a specific id — queue just that requirement.
  const handleDeleteSingle = (id: string) => {
    setPendingDelete([id]);
  };

  // Actually delete once the user confirms in the dialog.
  const handleConfirmDelete = async () => {
    const ids = pendingDelete;
    setPendingDelete(null);
    if (!ids) return;
    // Sequential, and only report success once every delete came back: the
    // deletes now reach the backend, so a partial failure must not be announced
    // as a completed deletion.
    let allOk = true;
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      allOk = (await store.deleteRequirement(id)) && allOk;
    }
    // Clear currentReqId if the currently-viewed requirement was deleted.
    if (store.currentReqId && ids.includes(store.currentReqId)) {
      store.setCurrentReqId(null);
    }
    if (allOk) {
      setMessage({ key: 'common.messages.deleted' });
    }
  };

  const handleApprove = (id: string) => {
    setApproveConfirmId(id);
  };

  const handleApproveConfirm = async () => {
    if (!approveConfirmId) return;
    const ok = await store.approveRequirement(approveConfirmId);
    setApproveConfirmId(null);
    if (ok) {
      setMessage({ key: 'common.messages.success' });
    }
  };

  const handleUnapprove = async (id: string) => {
    const ok = await store.unapproveRequirement(id);
    if (ok) {
      setMessage({ key: 'common.messages.success' });
    }
  };

  const handleCompleteAnalysis = async () => {
    // Pull the final session state and map results (scores + broken rules) onto
    // the requirements so the S4 review screen shows real analysis data.
    const sessionId = store.analysisSession?.id;
    if (sessionId) {
      try {
        const session = await api.getAnalysisSession(sessionId);
        store.applyAnalysisResults(session.jobs);
      } catch (err) {
        console.error('Failed to load final analysis results:', err);
        setMessage({ key: 'common.messages.error' });
        return;
      }
    }
    setScreen('review');
  };

  // Stops the analysis on the backend and keeps whatever finished before the
  // stop. The table is reached in every case: a failed cancel reports an error
  // but must not leave the user stuck on this screen, which is what returning
  // early (as handleCompleteAnalysis does) would do. The reload afterwards picks
  // up the DRAFT -> WIP move the backend made for the jobs that completed, which
  // applyAnalysisResults deliberately leaves to the server.
  const handleCancelAnalysis = async () => {
    const sessionId = store.analysisSession?.id;
    // Marking the session cancelled first stops the polling and disables the
    // button while the request is in flight.
    store.cancelAnalysisSession();
    try {
      if (sessionId) {
        const session = await api.cancelAnalysisSession(sessionId);
        store.applyAnalysisResults(session.jobs);
      }
    } catch (err) {
      console.error('Failed to cancel analysis:', err);
      setMessage({
        key: 's2.messages.cancelFailed',
        detail: err instanceof Error ? err.message : undefined,
      });
    } finally {
      backToSelect();
    }
    await store.loadProjectRequirements(projectId);
  };

  const handleFixRequiredWithOptions = (id: string) => {
    store.setCurrentReqId(id);
    setScreen('fixOptions');
  };

  const handleFixOptionsApply = (actions: FixAction[]) => {
    setSelectedFixActions(actions);
    setScreen('applyingFixes');
  };

  // Offers the fix's rewrite for review. A failure is reported here and
  // otherwise swallowed: the screen still shows the rewrite, and with nothing
  // pending, Accept saves it as a plain edit.
  const proposeForReview = async (
    threadId: string,
    currentText: string,
    text: string,
  ) => {
    try {
      await rewrite.propose(threadId, currentText, text);
    } catch (error) {
      setMessage({
        key: 'common.messages.error',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handleApplyingFixesComplete = async (
    fixedText?: string,
    notes?: string,
    skipped?: api.FixSkippedSuggestion[],
  ) => {
    if (fixedText) {
      store.setLastFixedText(fixedText);
      const threadId = currentRequirement?.threadId;
      if (threadId !== undefined) {
        await proposeForReview(
          threadId,
          currentRequirement?.text ?? '',
          fixedText,
        );
      } else {
        rewrite.clear();
      }
    }
    // Set unconditionally: a rerun that did rewrite the text has to clear the
    // note and skipped list left by the previous attempt.
    store.setLastFixNotes(notes ?? null);
    store.setLastFixSkipped(skipped ?? []);
    setSavedForAnalysis(null);
    setScreen('edit');
  };

  // S7 "Regenerate": re-run the backend fix with the same selected actions and
  // return the fresh corrected text for the editor to display.
  const handleRegenerate = async (): Promise<string | undefined> => {
    const req = currentRequirement;
    if (!req || req.threadId === undefined || req.versionId === undefined) {
      return undefined;
    }
    const actionIds = api.toFixActionIds(selectedFixActions.map(a => a.id));
    try {
      const res = await api.fixRequirement(
        req.threadId,
        req.versionId,
        actionIds,
      );
      store.setLastFixedText(res.afterText);
      store.setLastFixNotes(res.notes ?? null);
      store.setLastFixSkipped(res.skipped ?? []);
      // Propose again. The backend discards the proposal this one replaces, so
      // only the newest is ever up for review. When this run proposes nothing
      // (unchanged text, or the request failed) the previous proposal is
      // dropped too, so Accept cannot promote text that is no longer on screen.
      await proposeForReview(req.threadId, req.text, res.afterText);
      return res.afterText;
    } catch (err) {
      console.error('Regenerate failed:', err);
      return undefined;
    }
  };

  const handleForceApprove = () => {
    setForceApprovalOpen(true);
  };

  // Runs force-approve over a set of requirements and reports how many went
  // through. Only what the ASPICE gate held back comes back to the caller —
  // the gate answers HTTP 200 without approving, so treating a resolved call as
  // success would claim work the backend did not do. Failures are left to the
  // error banner the store already raised: they carry no warnings to read, and
  // offering "approve anyway" on them would re-send a request that fails the
  // same way while looking like a quality decision.
  const runForceApprove = async (ids: string[], confirm: boolean) => {
    const outcomes = await Promise.all(
      ids.map(id => store.forceApproveRequirement(id, confirm)),
    );
    const succeeded = outcomes.filter(o => o.approved).length;
    if (succeeded > 0) {
      setMessage({
        key: 'common.messages.forceApproved',
        params: { count: succeeded },
      });
    }
    return outcomes.filter(o => !o.approved && o.reason === 'gate');
  };

  const handleForceApprovalConfirm = async () => {
    // Force Approve is only reachable from the select table, so it acts on the
    // table's selection. `currentReqId` belongs to the detail screens and is
    // cleared on the way back here, but it is deliberately not consulted: this
    // action's scope is the selection the user can see.
    const blocked = await runForceApprove(Array.from(store.selectedIds), false);
    setForceApprovalOpen(false);
    if (blocked.length > 0) {
      setGateBlocked(blocked);
    }
  };

  // Second pass: the user has read the gate's warnings and chose to approve the
  // held-back requirements regardless.
  const handleApproveDespiteWarnings = async () => {
    const ids = gateBlocked.map(o => o.reqId);
    setGateBlocked([]);
    await runForceApprove(ids, true);
  };

  return (
    <>
      {/* Inside the view, not above it: the picker has to be locked while work
          is in flight, and that state lives here. Switching project unmounts
          this view and the in-flight response would be dropped silently. */}
      <ActiveProjectBar
        disabled={store.loading || redundancyLoading || mergeBusy}
      />

      {/* Auth Error */}
      {authError && <div className={classes.alertError}>❌ {authError}</div>}

      {/* Polling / transient error (auto-clears after 5 s) */}
      {pollingError && (
        <div className={classes.alertWarning}>
          ⚠️ {pollingError}
          <button
            type="button"
            aria-label={t('common.buttons.close')}
            onClick={() => setPollingError(null)}
            className={classes.alertCloseButton}
          >
            ✕
          </button>
        </div>
      )}

      {/* Load/API failures. The table renders `apiError` itself, so this only
          covers the screens that do not: a failed first load leaves the user on
          Import, where without this the requirements simply appear not to
          exist. */}
      {store.apiError && screen !== 'select' && (
        <div className={classes.alertError}>
          ❌ {store.apiError}
          <button
            type="button"
            aria-label={t('common.buttons.close')}
            onClick={store.clearApiError}
            className={classes.alertCloseButton}
          >
            ✕
          </button>
        </div>
      )}

      {/* Message — resolved with t() at render time so it follows the active
            language, and dismissible via the close button. */}
      {message && (
        <div className={classes.alertSuccess}>
          {message.detail
            ? `${t(message.key, message.params)}: ${message.detail}`
            : t(message.key, message.params)}
          <button
            type="button"
            aria-label={t('common.buttons.close')}
            onClick={() => setMessage(null)}
            className={classes.alertCloseButton}
          >
            ✕
          </button>
        </div>
      )}

      {/* Content Container */}
      <div className={classes.contentCard}>
        {screen === 'loading' && <Progress />}

        {/* S0: Import */}
        {screen === 'import' && canWrite && (
          <div>
            <ImportForm
              projectId={projectId}
              onImport={handleImport}
              onError={setAuthError}
            />
          </div>
        )}

        {/* S1: Select — analyze/delete actions live inside RequirementTable. */}
        {screen === 'select' && (
          <div>
            <RequirementTable
              requirements={store.requirements}
              selectedIds={store.selectedIds}
              onSelectAll={store.selectAll}
              onToggleSelect={store.toggleSelect}
              onAnalyze={handleAnalyze}
              onDelete={handleDeleteSelected}
              onForceApprove={handleForceApprove}
              onDetectRedundancies={handleDetectRedundancies}
              onImportMore={() => setScreen('import')}
              canWrite={canWrite}
              canApprove={canApprove}
              // A reload after import or a project switch keeps the table on
              // screen; without this it briefly reads as "no requirements".
              loading={store.loading}
              error={store.apiError}
              onClearError={store.clearApiError}
            />
          </div>
        )}

        {screen === 'redundancies' && (
          <div>
            <RedundancyProposals
              loading={redundancyLoading}
              error={redundancyError}
              proposals={redundancyProposals}
              excluded={excludedFromRedundancy}
              requirements={store.requirements}
              busy={mergeBusy}
              canWrite={canWrite}
              onMerge={handleMergeProposal}
              onRetry={handleDetectRedundancies}
              onBack={backToSelect}
            />
          </div>
        )}

        {/* S2: Analyzing */}
        {screen === 'analyzing' && (
          <div>
            <AnalysisProgress
              session={store.analysisSession}
              onCancel={handleCancelAnalysis}
              onComplete={handleCompleteAnalysis}
              onSessionUpdate={store.replaceAnalysisSession}
              onError={setPollingError}
            />
          </div>
        )}

        {/* S4: Review */}
        {screen === 'review' && (
          <div>
            <AnalysisReview
              requirements={selectedRequirements}
              currentReqId={
                store.currentReqId ?? selectedRequirements[0]?.id ?? null
              }
              onSelectRequirement={store.setCurrentReqId}
              onApprove={handleApprove}
              onUnapprove={handleUnapprove}
              onDelete={handleDeleteSingle}
              onFixRequired={handleFixRequiredWithOptions}
              canWrite={canWrite}
              canApprove={canApprove}
              onBack={backToSelect}
            />
          </div>
        )}

        {/* S5: Fix Options */}
        {screen === 'fixOptions' && currentRequirement && (
          <div>
            <FixOptionsScreen
              requirement={currentRequirement}
              onApply={handleFixOptionsApply}
              onBack={() => setScreen('review')}
            />
          </div>
        )}

        {/* S6: Applying Fixes */}
        {screen === 'applyingFixes' && currentRequirement && (
          <div>
            <ApplyingFixesScreen
              requirement={currentRequirement}
              selectedActions={selectedFixActions}
              onCancel={() => setScreen('fixOptions')}
              onComplete={handleApplyingFixesComplete}
            />
          </div>
        )}

        {/* S7: Edit manually */}
        {screen === 'edit' && currentRequirement && (
          <div>
            <ManualEditor
              key={`${currentRequirement.id}-${store.lastFixedText ?? 'orig'}`}
              requirement={currentRequirement}
              fixedText={store.lastFixedText ?? undefined}
              fixNotes={store.lastFixNotes ?? undefined}
              skippedSuggestions={store.lastFixSkipped}
              onRegenerate={handleRegenerate}
              hasPendingProposal={rewrite.hasPendingProposal}
              savedAwaitingAnalysis={
                savedForAnalysis !== null &&
                savedForAnalysis === currentRequirement.text
              }
              onCancel={async () => {
                // Turning a rewrite down rejects the proposal, so the refusal
                // is auditable. Without a proposal the AI offered nothing to
                // decline and this is a plain "go back".
                if (currentRequirement.threadId) {
                  try {
                    await rewrite.decline(currentRequirement.threadId);
                  } catch (error) {
                    // Stay on the screen: leaving would abandon the proposal
                    // without the record this call was meant to create.
                    setMessage({
                      key: 'common.messages.error',
                      detail:
                        error instanceof Error ? error.message : undefined,
                    });
                    return;
                  }
                }
                setSavedForAnalysis(null);
                setScreen('review');
              }}
              onApplyFix={async (before, after) => {
                // Accepting promotes the proposal to CURRENT, so the
                // re-analysis below reads the accepted text. An edited
                // proposal is accepted and the edit saved after it.
                // A retry of a text that is already saved skips the save
                // and only starts the analysis again.
                const alreadySaved = savedForAnalysis === after;
                let newVersionId: string | undefined;
                if (currentRequirement.threadId && !alreadySaved) {
                  try {
                    newVersionId = await rewrite.accept(
                      currentRequirement.threadId,
                      after,
                      accepted => {
                        // The proposal is CURRENT on the backend from here on,
                        // even if saving the edit below fails. Reflect it now,
                        // so leaving the screen — Cancel, or a Regenerate that
                        // fixes the CURRENT version — acts on what the backend
                        // has. lastFixedText follows the user's text so the
                        // editor keeps their edit for a retry instead of
                        // resetting to the requirement's new text.
                        store.setLastFixedText(after);
                        store.applyFix(
                          currentRequirement.id,
                          before,
                          accepted.text,
                          accepted.versionId,
                        );
                      },
                    );
                  } catch (error) {
                    // Surface what the backend said. A thread approved in
                    // another tab still answers 409 thread-not-editable here,
                    // and "エラー" alone leaves the user with nowhere to go.
                    setMessage({
                      key: 'common.messages.error',
                      detail:
                        error instanceof Error ? error.message : undefined,
                    });
                    return;
                  }
                }
                if (!alreadySaved) {
                  store.applyFix(
                    currentRequirement.id,
                    before,
                    after,
                    newVersionId,
                  );
                }
                const ok = await store.startAnalysisSession(
                  [currentRequirement.id],
                  projectId,
                );
                if (ok) {
                  setSavedForAnalysis(null);
                  setScreen('analyzing');
                } else {
                  setSavedForAnalysis(after);
                  setMessage({ key: 'common.messages.error' });
                }
              }}
            />
          </div>
        )}
      </div>

      {/* S3: Force Approval */}
      {forceApprovalOpen && (
        <ForceApprovalDialog
          open={forceApprovalOpen}
          requirements={selectedRequirements}
          warnings={selectedRequirements.flatMap(r =>
            (r.analysis?.brokenRules ?? []).map(br => ({
              ruleId: br.rule,
              severity: br.severity,
              recommendation: br.fix,
              requirementId: r.id,
            })),
          )}
          onConfirm={handleForceApprovalConfirm}
          onCancel={() => setForceApprovalOpen(false)}
        />
      )}

      {/* The ASPICE fast-check gate held some requirements back. Reuses the
          Lifecycle screen's dialog so both screens present the gate the same
          way. */}
      <ApprovalWarningsDialog
        open={gateBlocked.length > 0}
        // Tag each warning with the requirement it came from. Force approve
        // here acts on the whole selection, so without this the dialog is a
        // flat list the user cannot map back to anything.
        warnings={gateBlocked.flatMap(o =>
          o.warnings.map(w => ({ ...w, requirementId: o.reqId })),
        )}
        onCancel={() => setGateBlocked([])}
        onApproveAnyway={handleApproveDespiteWarnings}
      />

      {/* Approve confirmation dialog */}
      <ApproveConfirmDialog
        open={approveConfirmId !== null}
        requirement={
          approveConfirmId
            ? store.requirements.find(r => r.id === approveConfirmId) ?? null
            : null
        }
        appliedFixLabels={selectedFixActions.map(a => a.label)}
        onConfirm={handleApproveConfirm}
        onCancel={() => setApproveConfirmId(null)}
      />

      {/* Delete confirmation dialog (shared by S1 batch delete and S4 single delete) */}
      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
      >
        <DialogTitle>{t('common.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('common.deleteConfirm.body', {
              count: pendingDelete?.length ?? 0,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} variant="outlined">
            {t('common.deleteConfirm.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="secondary"
          >
            {t('common.deleteConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * SC-U16. Like the lifecycle screen, this keeps its own top-level route and
 * takes the project from the active-project context rather than the path.
 */
export const RequirementDoctorPage: React.FC = () => {
  const { t } = useTranslation('requirement-doctor');

  useSetTopBarTitle(t('title'), t('subtitle'));

  return (
    <Page themeId="home">
      <Content>
        <RequireActiveProject>
          {projectId => (
            // Keyed by project: imported requirements and `selectedIds` are
            // per-project, and carrying them across a switch would send one
            // project's thread ids to another project's analysis call.
            <RequirementDoctorView key={projectId} projectId={projectId} />
          )}
        </RequireActiveProject>
      </Content>
    </Page>
  );
};

export default RequirementDoctorPage;
